import { Badge } from "@/components/ui/badge"
import { Field, Fields } from "@/components/ui/panel"
import { display } from "@/lib/format"

import type { CredentialRow, Links, Transport } from "../types"

import { CheckRow } from "./checks"

/**
 * Reference, not diagnosis: how this environment is wired. Deliberately the
 * quietest band on the page — always visible and Cmd-F-able, but flat, so it
 * never competes with a verdict.
 */
export function Reference({
  transport,
  credentials,
  links,
}: {
  transport: Transport
  credentials: CredentialRow[]
  links: Links
}) {
  return (
    <section
      id="config"
      tabIndex={-1}
      aria-labelledby="config-heading"
      className="border-secondary flex scroll-mt-24 flex-col gap-5 border-t pt-6 outline-none"
    >
      <div className="flex flex-col gap-0.5">
        <h2 id="config-heading" className="text-primary text-md font-semibold">
          Configuration
        </h2>
        <p className="text-tertiary text-sm">
          Read live from this process. Credentials show presence only; the page
          never reads a value.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-x-8 gap-y-6 lg:grid-cols-2">
        <div className="flex flex-col gap-3">
          <h3 className="eyebrow text-tertiary">Transport</h3>
          <Fields>
            <Field label="Environment">{transport.rails_env}</Field>
            <Field label="Delivery method">
              {transport.matches_matrix ? (
                transport.delivery_method
              ) : (
                <span className="text-fg-error-primary font-medium">
                  {transport.delivery_method}
                </span>
              )}
            </Field>
            <Field label="From">{transport.from}</Field>
            <Field label="Reply-To">{transport.reply_to}</Field>
            <Field label="Links resolve to">
              {display(transport.link_url)}
            </Field>
            <Field label="Sends real mail">
              {transport.live_provider ? "Yes" : "No"}
            </Field>
            {transport.live_route && (
              <Field label="Live route">{transport.live_route}</Field>
            )}
            {transport.sandbox_route && (
              <Field label="Sandbox route">{transport.sandbox_route}</Field>
            )}
          </Fields>
          {transport.message_defaults && (
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(transport.message_defaults).map(
                ([key, value]) => (
                  <Badge key={key} type="modern" color="gray" size="sm">
                    {key}: {value}
                  </Badge>
                ),
              )}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-3">
          <h3 className="eyebrow text-tertiary">Credentials</h3>
          <p className="text-tertiary text-sm">
            The boot check warns about some of these and never fails on any of
            them.
          </p>
          <ul className="flex flex-col gap-2">
            {credentials.map((credential) => (
              <CheckRow
                key={credential.key}
                state={credential.applicable ? credential.present : null}
                label={credential.key}
                detail={
                  credential.applicable
                    ? credential.present
                      ? "Set."
                      : `Missing. ${credential.breaks}`
                    : (credential.detail_when_inapplicable ?? "Not used here.")
                }
              />
            ))}
          </ul>
        </div>
      </div>

      <p className="text-tertiary text-xs">
        Provenance:{" "}
        <a
          href={links.jira_ticket}
          target="_blank"
          rel="noreferrer"
          className="hover:text-secondary underline decoration-transparent transition-colors hover:decoration-current"
        >
          BLU-9
        </a>{" "}
        ·{" "}
        <a
          href={links.jira_epic}
          target="_blank"
          rel="noreferrer"
          className="hover:text-secondary underline decoration-transparent transition-colors hover:decoration-current"
        >
          BLU-4
        </a>
      </p>
    </section>
  )
}
